import {useRequiredConfig} from '@domain/shifthandover'
import {FlexPage} from '@hconnect/common/components'
import {ShiftNotFoundError} from '@hconnect/common/types'
import {useIsMobile} from '@hconnect/uikit'
import {BaseErrorPage} from '@hconnect/uikit/src/lib2/pages/BaseErrorPage'
import {Grid} from '@mui/material'
import {defaultDesktop, getFilterDifference, Page} from '@widgets/shift-event-filters'
import {TFunction} from 'i18next'
import React, {Component, FC, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {ReactComponent as Illustration} from '../../assets/images/notAvailable.svg'
import {HLog} from '../../common/utils/logging'
import {useResolvePageFromLocation} from '../../hooks'
import {usePageFilters} from '../../hooks/useEventsFilter'
import {useTrackAnalyticsEvent} from '../../hooks/useTrackAnalyticsEvents'
import {EventsFilterBar} from '../../layouts/FilterBar'
import {getActionContentSXWithFilter} from '../../styles/common'

type ErrorBoundaryProps = {
  children: React.ReactNode
  t: TFunction
  isMobileFilterOpen: boolean
  fallback: (resetError: () => void) => React.ReactNode
}

type ErrorBoundaryState = {
  error: Error | null
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {error: null}
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {error}
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    HLog('cockpit').extras({info}).error(error)
  }

  render() {
    const {isMobileFilterOpen, fallback, t, children} = this.props

    if (this.state.error instanceof ShiftNotFoundError) {
      return (
        <FlexPage
          appName="Cockpit"
          title={isMobileFilterOpen ? t('shiftEvent.action.filter') : undefined}
          sxActionContent={getActionContentSXWithFilter(isMobileFilterOpen)}
          headerActionContent={
            <Grid container alignItems="center">
              <Grid item xs={12}>
                {fallback(() => this.setState({error: null}))}
              </Grid>
            </Grid>
          }
        >
          {!isMobileFilterOpen && (
            <BaseErrorPage
              illustration={<Illustration />}
              caption={t('errorboundary.noShiftAvailable')}
              description={t('errorboundary.selectValidDate')}
            />
          )}
        </FlexPage>
      )
    }

    return children
  }
}

type ShiftErrorBoundaryProps = {
  plantId: string
  children: React.ReactNode
}

export const ShiftErrorBoundary: FC<ShiftErrorBoundaryProps> = ({plantId, children}) => {
  const page = useResolvePageFromLocation()
  const {t} = useTranslation()
  const isMobile = useIsMobile()
  const [filterOptions, setFilters] = usePageFilters(plantId, page || Page.Events)
  const {country} = useRequiredConfig(plantId)
  const trackAnalyticsEvent = useTrackAnalyticsEvent({plantId, country})
  const [isMobileFilterOpen, openMobileFilter] = useState(false)
  if (!page) return children

  return (
    <ErrorBoundary
      key={page}
      t={t}
      isMobileFilterOpen={isMobileFilterOpen}
      fallback={(resetError) => (
        <EventsFilterBar<'timeRange'>
          plantId={plantId}
          activeSettings={filterOptions}
          isCollapsible={isMobile}
          options={[]}
          onChange={(nextFilterOptions) => {
            setFilters(nextFilterOptions)
            resetError()
            trackAnalyticsEvent('userAdjustsFilter', {
              filterPage: page,
              filterName: getFilterDifference(nextFilterOptions, filterOptions)
            })
          }}
          openMobileFilter={openMobileFilter}
          isMobileFilterOpen={isMobileFilterOpen}
          defaultFilters={defaultDesktop}
        />
      )}
    >
      {children}
    </ErrorBoundary>
  )
}
